import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Martini } from "lucide-react";

import { LoginForm } from "@/features/auth";

import "./LandingPage.css";
import "./LoginPage.css";

export function LoginPage() {
  const [params] = useSearchParams();
  const justVerified = params.get("verified") === "1";

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-2">
      {/* Brand panel — landing page hero */}
      <aside className="relative hidden overflow-hidden lg:flex">
        <div className="os-landing login-os-landing w-full">
          <section className="hero">
            <div className="hero-inner">
              <div className="hero-copy">
                <Link
                  to="/"
                  className="logo-text"
                  aria-label="OneShift home"
                  style={{
                    display: "inline-block",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 22,
                    letterSpacing: "-0.02em",
                    marginBottom: 32,
                  }}
                >
                  one<span style={{ color: "var(--yellow)" }}>Shift</span>
                </Link>

                <div className="hero-tag" data-anim>
                  <span className="live-dot"></span>
                  Now in beta · Ottawa, Ontario
                </div>

                <h1 data-anim data-d="1">
                  Ottawa's platform for{" "}
                  <span className="kw kw-pink">workers</span>,{" "}
                  <span className="kw kw-teal">venues</span>, and{" "}
                  <span className="kw kw-yellow">great shifts</span>.
                </h1>

                <p className="hero-sub" data-anim data-d="2">
                  Skilled hospitality staff, background-checked and rated, available same-day.
                  No agency markup. No middlemen.
                </p>

                <div className="hero-ctas" data-anim data-d="3">
                  <Link to="/signup" className="btn btn-yellow">Find Shifts →</Link>
                  <Link to="/signup" className="btn btn-outline-white">Post a Shift</Link>
                </div>

                <div className="hero-proof" data-anim data-d="4">
                  <div className="proof-badge">
                    <div>
                      <div className="pb-num">100%</div>
                      <div className="pb-label">Background checked</div>
                    </div>
                  </div>
                  <div className="pb-divider"></div>
                  <div className="proof-badge">
                    <div>
                      <div className="pb-num">$0</div>
                      <div className="pb-label">Agency markup</div>
                    </div>
                  </div>
                  <div className="pb-divider"></div>
                  <div className="proof-badge">
                    <div>
                      <div className="pb-num">&lt;10 min</div>
                      <div className="pb-label">Target time to fill</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Animated scene */}
              <div className="hero-scene" aria-hidden="true">
                <span className="scene-particle sp-1"></span>
                <span className="scene-particle sp-2"></span>
                <span className="scene-particle sp-3"></span>
                <span className="scene-particle sp-4"></span>

                <div className="scene-beam"></div>

                {/* 1) Shift posted */}
                <div className="scene-card scene-post">
                  <div className="scene-post-head">
                    <div className="scene-post-avatar">
                      <Martini size={18} strokeWidth={2.4} aria-hidden />
                    </div>
                    <div className="scene-post-meta">
                      <div className="scene-post-venue">The Copper Fox</div>
                      <div className="scene-post-time">Posted 2 min ago</div>
                    </div>
                    <div className="scene-post-tag">Urgent</div>
                  </div>
                  <div className="scene-post-title">Bartender — tonight 6pm</div>
                  <div className="scene-post-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    ByWard Market · 1.2 km
                  </div>
                  <div className="scene-post-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    6:00 pm – 12:00 am · 6 hrs
                  </div>
                  <div className="scene-post-pay">
                    <span className="scene-post-pay-label">Total</span>
                    <span className="scene-post-pay-num">$162</span>
                  </div>
                </div>

                {/* 2) Workers matching */}
                <div className="scene-card scene-match">
                  <div className="scene-match-label">
                    <span className="dot"></span> 3 matched · live
                  </div>
                  <div className="scene-match-list">
                    <div className="scene-match-row">
                      <div className="scene-match-avatar ava-1">M</div>
                      <div className="scene-match-info">
                        <div className="scene-match-name">Maya T.</div>
                        <div className="scene-match-stars"><span className="star">★</span> 4.9 · 142 shifts</div>
                      </div>
                      <span className="scene-match-dist">0.8 km</span>
                    </div>
                    <div className="scene-match-row">
                      <div className="scene-match-avatar ava-2">D</div>
                      <div className="scene-match-info">
                        <div className="scene-match-name">Daniel O.</div>
                        <div className="scene-match-stars"><span className="star">★</span> 4.8 · 96 shifts</div>
                      </div>
                      <span className="scene-match-dist">1.4 km</span>
                    </div>
                    <div className="scene-match-row">
                      <div className="scene-match-avatar ava-3">S</div>
                      <div className="scene-match-info">
                        <div className="scene-match-name">Sara L.</div>
                        <div className="scene-match-stars"><span className="star">★</span> 5.0 · 58 shifts</div>
                      </div>
                      <span className="scene-match-dist">2.1 km</span>
                    </div>
                  </div>
                </div>

                {/* 3) Confirmed */}
                <div className="scene-card scene-confirm">
                  <div className="scene-confirm-head">
                    <div className="scene-confirm-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="5 12 10 17 19 8" />
                      </svg>
                    </div>
                    <div>
                      <div className="scene-confirm-title">Shift confirmed</div>
                      <div className="scene-confirm-sub">Filled in 4 minutes</div>
                    </div>
                  </div>
                  <div className="scene-confirm-body">
                    <div className="scene-match-avatar ava-1">M</div>
                    <div>
                      <div className="scene-confirm-name">Maya is on her way</div>
                      <div className="scene-confirm-eta">ETA 5:48 pm · GPS tracked</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </aside>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2 text-center lg:text-left">
            <h1 className="text-3xl font-semibold tracking-display-tight text-copy">
              Sign in to OneShift
            </h1>
            <p className="text-sm text-copy-muted">
              Pick up where you left off. Your shifts, your team, your earnings.
            </p>
          </div>

          {justVerified && (
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-success bg-success-light p-3 text-sm"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-foreground" />
              <div>
                <p className="font-semibold text-success-foreground">Email verified</p>
                <p className="text-success-foreground">Sign in below to continue.</p>
              </div>
            </div>
          )}

          <LoginForm />

          <p className="text-center text-xs text-copy-muted">
            <a
              href="/legal/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-copy-muted underline underline-offset-2 hover:text-copy"
            >
              Terms
            </a>
            {" · "}
            <a
              href="/legal/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-copy-muted underline underline-offset-2 hover:text-copy"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
